import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  ControlButton,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  ReactFlowProvider
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import '../workflow/workflowEditor.css';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { ArrowLeft, Braces, Copy, Map as MapIcon, Save, Trash2, Wrench } from 'lucide-react';
import { useAuthContext } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/api';
import BleuEdge, { BleuConnectionLine } from '../workflow/BleuEdge';
import { wfNodeTypes, PALETTE_NODE_TYPES, EVENT_PALETTE_META, TEXT_GEN_MODELS } from '../workflow/WfNodes';
import { getPaletteEventsForCategory, BOTS_OPTIONAL_TELEGRAM_EVENTS } from '../workflow/workflowTemplates';
import {
  mergeGraphWithMeta,
  splitGraphMeta,
  newNodeId,
  newEdgeId,
  defaultGraph,
  defaultGraphMeta,
  pinBus,
  targetHandleForDroppedWire
} from '../workflow/workflowGraphModel';

const edgeTypes = { bleu: BleuEdge };

const DND_TYPE = 'application/wfNodeType';

/** Keyboard hints for context menus (Bleu-style: instant menu + shortcut column). */
function getCtxMenuKeyHints() {
  if (typeof navigator === 'undefined') {
    return { copy: 'Ctrl+C', del: 'Del' };
  }
  const mac = /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (mac) {
    return { copy: '⌘C', del: '⌫' };
  }
  return { copy: 'Ctrl+C', del: 'Del / ⌫' };
}

const CTX_MENU_PAPER_SX = {
  borderRadius: '8px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  backgroundImage: 'none',
  boxShadow: '0 10px 40px rgba(0,0,0,0.45)'
};

const CTX_ADD_NODE_ITEM_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  py: 0.75,
  px: 1,
  minHeight: 0,
  borderRadius: '6px'
};

const CTX_NODE_ACTION_ITEM_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  py: 0.625,
  px: 1,
  minHeight: 0,
  borderRadius: '6px'
};

const CTX_MENU_SHORTCUT_SX = {
  fontSize: '0.6875rem',
  lineHeight: 1.2,
  color: 'rgba(161, 161, 170, 0.92)',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  letterSpacing: '0.04em',
  ml: 'auto',
  pl: 1.5,
  flexShrink: 0,
  opacity: 0.95,
  whiteSpace: 'nowrap'
};

function CtxMenuShortcut({ children }) {
  if (children == null || children === '') return null;
  return (
    <Typography component="span" variant="caption" sx={CTX_MENU_SHORTCUT_SX}>
      {children}
    </Typography>
  );
}

function isFlowEntryEventType(t) {
  return t === 'wfTrigger' || (typeof t === 'string' && t.startsWith('wfEventTg'));
}

function defaultDataForType(type, extra) {
  if (type === 'wfTrigger') return { label: 'Start', hint: 'Entry point' };
  if (type === 'wfOutput') return { label: 'End', hint: 'Terminal' };
  if (type === 'wfTextGen') return { label: 'Text Generation', hint: 'LLM step', model: 'gpt-4o-mini' };
  if (type === 'wfToolRef') {
    const desc = typeof extra?.description === 'string' ? extra.description.trim() : '';
    return {
      label: extra?.name != null && String(extra.name).trim() ? String(extra.name).trim() : 'Tool',
      toolId: extra?.toolId != null ? String(extra.toolId) : '',
      description: desc,
      hint: ''
    };
  }
  if (typeof type === 'string' && type.startsWith('wfEventTg')) {
    const entry = BOTS_OPTIONAL_TELEGRAM_EVENTS.find((e) => e.type === type);
    return entry ? { ...entry.defaultData } : { label: 'Telegram', hint: 'Event', telegramKind: 'text' };
  }
  return { label: 'Action', hint: 'Worker step' };
}

function cloneState(nodes, edges) {
  return {
    nodes: nodes.map((n) => JSON.parse(JSON.stringify(n))),
    edges: edges.map((e) => JSON.parse(JSON.stringify(e)))
  };
}

// ── Orphan edge cleanup helper ─────────────────────────────────
// Drops edges whose source/target node is missing or whose handle doesn't
// exist on the resolved node, per the handles each workflow node type
// actually renders in WfNodes.js. Unknown node types → keep (conservative).
function handlesForWfNode(node) {
  if (!node) return null;
  switch (node.type) {
    case 'wfTrigger':
      return { src: new Set(['flow-out']), tgt: new Set() };
    case 'wfAction':
      return {
        src: new Set(['flow-out', 'data-out']),
        tgt: new Set(['flow-in', 'data-in'])
      };
    case 'wfOutput':
      return { src: new Set(), tgt: new Set(['flow-in', 'data-in']) };
    case 'wfTextGen':
      return {
        src: new Set(['flow-out']),
        tgt: new Set(['flow-in', 'data-prompt', 'tools-in'])
      };
    case 'wfToolRef':
      return { src: new Set(['tool-out']), tgt: new Set() };
    case 'wfEventTgText':
    case 'wfEventTgPhoto':
    case 'wfEventTgVideo':
    case 'wfEventTgFile':
    case 'wfEventTgSticker':
    case 'wfEventTgAnimation':
      return { src: new Set(['flow-out']), tgt: new Set() };
    default:
      return null;
  }
}

function filterOrphanEdges(edgesList, nodesList) {
  const nodeById = new Map((nodesList || []).map((n) => [n.id, n]));
  let removed = 0;
  const unknownTypes = new Set();
  const kept = (edgesList || []).filter((e) => {
    const src = nodeById.get(e.source);
    const tgt = nodeById.get(e.target);
    if (!src || !tgt) { removed++; return false; }
    const srcHandles = handlesForWfNode(src);
    const tgtHandles = handlesForWfNode(tgt);
    if (srcHandles === null && src.type) unknownTypes.add(src.type);
    if (tgtHandles === null && tgt.type) unknownTypes.add(tgt.type);
    const sh = e.sourceHandle || 'flow-out';
    const th = e.targetHandle || 'flow-in';
    if (srcHandles && !srcHandles.src.has(sh)) { removed++; return false; }
    if (tgtHandles && !tgtHandles.tgt.has(th)) { removed++; return false; }
    return true;
  });
  if (unknownTypes.size > 0) {
    console.error(
      `[Workflow] handlesForWfNode is missing entries for node type(s): ${[...unknownTypes].join(', ')}. Add them to handlesForWfNode() in WorkflowEditorPage.js so orphan edge cleanup validates their handles.`
    );
  }
  return { edges: kept, removed };
}

function WorkflowEditorCanvas({ workflow }) {
  const navigate = useNavigate();
  const { getToken } = useAuthContext();
  const { screenToFlowPosition, setViewport, getViewport } = useReactFlow();

  const merged = useMemo(
    () => mergeGraphWithMeta(workflow.graph || defaultGraph(), workflow.graphMeta || defaultGraphMeta()),
    [workflow.graph, workflow.graphMeta]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(merged.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(merged.edges);

  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;
  }, [nodes, edges]);

  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const viewportRestored = useRef(false);
  const saveTimer = useRef(null);
  const lastSavedJson = useRef('');

  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [viewportRev, setViewportRev] = useState(0);

  const [ctxMenu, setCtxMenu] = useState(null);
  const [nodeCtx, setNodeCtx] = useState(null);
  const [miniMapVisible, setMiniMapVisible] = useState(false);
  const [aiTools, setAiTools] = useState([]);

  const keyHints = useMemo(() => getCtxMenuKeyHints(), []);

  const paletteEventItems = useMemo(() => {
    return getPaletteEventsForCategory(workflow.categoryId || 'other').map((e) => {
      const meta = EVENT_PALETTE_META[e.type] || {};
      return {
        type: e.type,
        title: e.paletteTitle,
        subtitle: e.paletteSubtitle,
        Icon: meta.Icon,
        iconColor: meta.iconColor || '#a1a1aa',
        iconWellBg: meta.iconWellBg || 'rgba(161, 161, 170, 0.12)',
        section: e.section
      };
    });
  }, [workflow.categoryId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/ai-tools`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setAiTools(data.tools || []);
      } catch {
        if (!cancelled) setAiTools([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, workflow.id]);

  const paneRef = useRef(null);

  const pushHistory = useCallback(() => {
    undoStack.current.push(cloneState(nodesRef.current, edgesRef.current));
    if (undoStack.current.length > 100) undoStack.current.shift();
    redoStack.current = [];
  }, []);

  const undo = useCallback(() => {
    if (!undoStack.current.length) return;
    const snap = undoStack.current.pop();
    redoStack.current.push(cloneState(nodesRef.current, edgesRef.current));
    setNodes(snap.nodes);
    setEdges(snap.edges);
  }, [setNodes, setEdges]);

  const redo = useCallback(() => {
    if (!redoStack.current.length) return;
    const snap = redoStack.current.pop();
    undoStack.current.push(cloneState(nodesRef.current, edgesRef.current));
    setNodes(snap.nodes);
    setEdges(snap.edges);
  }, [setNodes, setEdges]);

  useEffect(() => {
    let nextNodes = merged.nodes;
    let nextEdges = merged.edges;
    if (nextNodes.length === 0) {
      const id = newNodeId();
      nextNodes = [
        {
          id,
          type: 'wfTrigger',
          position: { x: 80, y: 80 },
          data: defaultDataForType('wfTrigger'),
          selected: false
        }
      ];
      nextEdges = [];
    }
    setNodes(nextNodes);
    setEdges(nextEdges);
    undoStack.current = [];
    redoStack.current = [];
    viewportRestored.current = false;
    const { graph, graphMeta } = splitGraphMeta(nextNodes, nextEdges, merged.viewport);
    lastSavedJson.current = JSON.stringify({ graph, graphMeta });
  }, [workflow.id, merged, setNodes, setEdges]);

  useEffect(() => {
    if (viewportRestored.current) return;
    const meta = workflow.graphMeta && typeof workflow.graphMeta === 'object' ? workflow.graphMeta : {};
    const v = meta.viewport;
    if (v && typeof v.x === 'number' && typeof v.y === 'number' && typeof v.zoom === 'number') {
      setViewport({ x: v.x, y: v.y, zoom: v.zoom }, { duration: 0 });
    }
    viewportRestored.current = true;
  }, [workflow.id, workflow.graphMeta, setViewport]);

  const addNodeAt = useCallback(
    (type, flowPos, connectFrom, extra) => {
      if (type === 'wfTrigger' && nodesRef.current.some((n) => n.type === 'wfTrigger')) return;
      if (typeof type === 'string' && type.startsWith('wfEventTg')) {
        if (nodesRef.current.some((n) => n.type === type)) return;
      }
      pushHistory();
      const id = newNodeId();
      const base = {
        id,
        type,
        position: flowPos,
        data: defaultDataForType(type, extra),
        selected: true
      };
      setNodes((nds) => nds.map((n) => ({ ...n, selected: false })).concat(base));
      if (connectFrom && !isFlowEntryEventType(type)) {
        const targetHandle = targetHandleForDroppedWire(connectFrom.handleId, type);
        if (targetHandle) {
          setEdges((eds) =>
            eds.concat({
              id: newEdgeId(),
              type: 'bleu',
              source: connectFrom.nodeId,
              sourceHandle: connectFrom.handleId || 'flow-out',
              target: id,
              targetHandle
            })
          );
        }
      }
    },
    [pushHistory, setNodes, setEdges]
  );

  const isValidConnection = useCallback((conn) => {
    if (conn.source === conn.target) return false;
    const targetNode = nodesRef.current.find((n) => n.id === conn.target);
    const sourceNode = nodesRef.current.find((n) => n.id === conn.source);
    if (targetNode?.type === 'wfTrigger' || targetNode?.type?.startsWith?.('wfEventTg')) return false;
    if (sourceNode?.type === 'wfOutput') return false;
    if (targetNode?.type === 'wfToolRef') return false;
    const s = pinBus(conn.sourceHandle);
    const t = pinBus(conn.targetHandle);
    if (s === 'tool' || t === 'tool') {
      return s === 'tool' && t === 'tool' && conn.targetHandle === 'tools-in';
    }
    return s === t;
  }, []);

  const onConnect = useCallback(
    (params) => {
      pushHistory();
      setEdges((eds) => addEdge({ ...params, id: newEdgeId(), type: 'bleu' }, eds));
    },
    [pushHistory, setEdges]
  );

  const onConnectEnd = useCallback(
    (event, connectionState) => {
      if (!connectionState.fromNode || connectionState.toNode) return;
      const clientX = 'changedTouches' in event ? event.changedTouches[0].clientX : event.clientX;
      const clientY = 'changedTouches' in event ? event.changedTouches[0].clientY : event.clientY;
      const flowPos = screenToFlowPosition({ x: clientX, y: clientY });
      const handleId = connectionState.fromHandle?.id ?? 'flow-out';
      setCtxMenu({
        mouseX: clientX,
        mouseY: clientY,
        flowPos,
        connectFrom: { nodeId: connectionState.fromNode.id, handleId }
      });
    },
    [screenToFlowPosition]
  );

  const onNodeDragStart = useCallback(() => {
    pushHistory();
  }, [pushHistory]);

  const onBeforeDelete = useCallback(
    async ({ nodes: toDel }) => {
      const delIds = new Set(toDel.map((n) => n.id));
      const triggersLeft = nodesRef.current.filter((n) => n.type === 'wfTrigger' && !delIds.has(n.id));
      if (toDel.some((n) => n.type === 'wfTrigger') && triggersLeft.length === 0) {
        return false;
      }
      pushHistory();
      return true;
    },
    [pushHistory]
  );

  const onPaneContextMenu = useCallback(
    (e) => {
      e.preventDefault();
      const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      setCtxMenu({ mouseX: e.clientX, mouseY: e.clientY, flowPos, connectFrom: null });
    },
    [screenToFlowPosition]
  );

  const onNodeContextMenu = useCallback(
    (e, node) => {
      e.preventDefault();
      setNodeCtx({ mouseX: e.clientX, mouseY: e.clientY, nodeId: node.id });
    },
    []
  );

  const closeCtxMenu = useCallback(() => setCtxMenu(null), []);
  const closeNodeCtx = useCallback(() => setNodeCtx(null), []);

  const pickNodeTypeFromMenu = useCallback(
    (type) => {
      if (!ctxMenu) return;
      addNodeAt(type, ctxMenu.flowPos, ctxMenu.connectFrom || undefined);
      closeCtxMenu();
    },
    [ctxMenu, addNodeAt, closeCtxMenu]
  );

  const pickToolFromMenu = useCallback(
    (tool) => {
      if (!ctxMenu) return;
      addNodeAt('wfToolRef', ctxMenu.flowPos, ctxMenu.connectFrom || undefined, {
        toolId: tool.id,
        name: tool.name,
        description: tool.description
      });
      closeCtxMenu();
    },
    [ctxMenu, addNodeAt, closeCtxMenu]
  );

  const addFromPaletteDragStart = useCallback((event, payload) => {
    const raw = typeof payload === 'string' ? payload : JSON.stringify(payload);
    event.dataTransfer.setData(DND_TYPE, raw);
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();

      // Handle JSON file drop — replaces entire graph
      const files = e.dataTransfer.files;
      if (files && files.length === 1 && files[0].name.endsWith('.json')) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          try {
            const parsed = JSON.parse(ev.target.result);
            const g = parsed.graph || parsed;
            const gm = parsed.graphMeta || { positions: {}, viewport: null };
            const m = mergeGraphWithMeta(g, gm);
            setNodes(m.nodes);
            setEdges(m.edges);
          } catch (err) {
            console.error('JSON import error:', err);
          }
        };
        reader.readAsText(files[0]);
        return;
      }

      // Handle palette node DnD
      const raw = e.dataTransfer.getData(DND_TYPE);
      if (!raw) return;
      const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      try {
        const j = JSON.parse(raw);
        if (j && j.kind === 'wfToolRef' && j.toolId) {
          addNodeAt('wfToolRef', flowPos, null, {
            toolId: j.toolId,
            name: j.name,
            description: j.description
          });
          return;
        }
      } catch {
        /* plain type id */
      }
      addNodeAt(raw, flowPos, null);
    },
    [screenToFlowPosition, addNodeAt, setNodes, setEdges]
  );

  const addFromPaletteClick = useCallback(
    (type, extra) => {
      const el = paneRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const flowPos = screenToFlowPosition({
        x: r.left + r.width / 2,
        y: r.top + r.height / 2
      });
      addNodeAt(type, flowPos, null, extra);
    },
    [screenToFlowPosition, addNodeAt]
  );

  const persist = useCallback(async () => {
    const vp = getViewport();
    // Scrub orphan / invalid-handle edges and dedupe outputs before saving.
    const { edges: orphansRemoved, removed } = filterOrphanEdges(edgesRef.current, nodesRef.current);
    const sourceKey = (e) => `${e.source}::${e.sourceHandle || 'flow-out'}`;
    const lastIdxByKey = new Map();
    orphansRemoved.forEach((e, i) => { lastIdxByKey.set(sourceKey(e), i); });
    const droppedDupes = [];
    const cleanedEdges = orphansRemoved.filter((e, i) => {
      const key = sourceKey(e);
      if (lastIdxByKey.get(key) === i) return true;
      droppedDupes.push({
        source: key,
        edgeId: e.id,
        target: `${e.target}::${e.targetHandle || 'flow-in'}`
      });
      return false;
    });
    if (droppedDupes.length > 0) {
      console.warn(
        `[Workflow] Removed ${droppedDupes.length} duplicate edge(s) on save (kept the last one per output pin):`,
        droppedDupes
      );
    }
    if (removed > 0 || droppedDupes.length > 0) setEdges(cleanedEdges);
    const { graph, graphMeta } = splitGraphMeta(nodesRef.current, cleanedEdges, vp);
    const payload = JSON.stringify({ graph, graphMeta });
    if (payload === lastSavedJson.current) return;
    setSaveError('');
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/workflows/${encodeURIComponent(workflow.id)}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ graph, graphMeta })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Save failed (${res.status})`);
      }
      lastSavedJson.current = payload;
    } catch (e) {
      setSaveError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [getToken, getViewport, workflow.id, setEdges]);

  const onMoveEnd = useCallback(() => {
    setViewportRev((r) => r + 1);
  }, []);

  useEffect(() => {
    const vp = getViewport();
    const { graph, graphMeta } = splitGraphMeta(nodes, edges, vp);
    const payload = JSON.stringify({ graph, graphMeta });
    if (payload === lastSavedJson.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      persist();
    }, 2000);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [nodes, edges, viewportRev, persist, getViewport]);

  const inspectorPushRef = useRef(false);
  const selectedNodes = useMemo(() => nodes.filter((n) => n.selected), [nodes]);
  const primarySelected = selectedNodes.length === 1 ? selectedNodes[0] : null;

  const updateSelectedData = useCallback(
    (patch) => {
      if (!primarySelected) return;
      if (!inspectorPushRef.current) {
        pushHistory();
        inspectorPushRef.current = true;
      }
      setNodes((nds) =>
        nds.map((n) => (n.id === primarySelected.id ? { ...n, data: { ...n.data, ...patch } } : n))
      );
    },
    [primarySelected, pushHistory, setNodes]
  );

  useEffect(() => {
    inspectorPushRef.current = false;
  }, [primarySelected?.id]);

  const deleteNodeById = useCallback(
    (id) => {
      pushHistory();
      setNodes((nds) => nds.filter((n) => n.id !== id));
      setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
    },
    [pushHistory, setNodes, setEdges]
  );

  const copySelection = useCallback(() => {
    const selected = nodesRef.current.filter((n) => n.selected);
    if (!selected.length) return;
    const ids = new Set(selected.map((n) => n.id));
    const subEdges = edgesRef.current.filter((e) => ids.has(e.source) && ids.has(e.target));
    const payload = { v: 1, nodes: selected, edges: subEdges };
    try {
      sessionStorage.setItem('flowcore_wf_clipboard', JSON.stringify(payload));
    } catch (e) {
      setSaveError('Clipboard write failed');
    }
  }, []);

  const pasteRef = useRef({ x: 0, y: 0 });

  const pasteFromClipboard = useCallback(() => {
    let raw;
    try {
      raw = sessionStorage.getItem('flowcore_wf_clipboard');
    } catch (e) {
      return;
    }
    if (!raw) return;
    let payload;
    try {
      payload = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!payload?.nodes?.length) return;
    const clipNodes = payload.nodes;
    const clipEdges = payload.edges || [];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of clipNodes) {
      const x = n.position?.x ?? 0;
      const y = n.position?.y ?? 0;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + 220);
      maxY = Math.max(maxY, y + 100);
    }
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const target = pasteRef.current;
    const dx = target.x - cx;
    const dy = target.y - cy;
    const idMap = new Map();
    pushHistory();
    for (const n of clipNodes) {
      idMap.set(n.id, newNodeId());
    }
    const newNodes = clipNodes.map((n) => ({
      ...JSON.parse(JSON.stringify(n)),
      id: idMap.get(n.id),
      position: { x: (n.position?.x ?? 0) + dx, y: (n.position?.y ?? 0) + dy },
      selected: true
    }));
    const newEdges = clipEdges.map((e) => ({
      ...JSON.parse(JSON.stringify(e)),
      id: newEdgeId(),
      source: idMap.get(e.source),
      target: idMap.get(e.target)
    }));
    setNodes((nds) => nds.map((n) => ({ ...n, selected: false })).concat(newNodes));
    setEdges((eds) => eds.concat(newEdges));
  }, [pushHistory, setNodes, setEdges]);

  useEffect(() => {
    function onMove(e) {
      pasteRef.current = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    }
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [screenToFlowPosition]);

  useEffect(() => {
    function onKeyDown(e) {
      const t = e.target;
      const tag = t?.tagName;
      const inField =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        t?.isContentEditable;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        if (inField) return;
        e.preventDefault();
        undo();
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))
      ) {
        if (inField) return;
        e.preventDefault();
        redo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        if (inField) return;
        e.preventDefault();
        copySelection();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        if (inField) return;
        e.preventDefault();
        pasteFromClipboard();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo, copySelection, pasteFromClipboard]);

  const jsonPreview = useMemo(() => {
    void viewportRev;
    const vp = getViewport();
    const { graph, graphMeta } = splitGraphMeta(nodes, edges, vp);
    return JSON.stringify({ graph, graphMeta }, null, 2);
  }, [nodes, edges, getViewport, viewportRev]);

  return (
    <Box className="wf-editor-root" sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#09090b' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          bgcolor: '#18181b',
          flexShrink: 0
        }}
      >
        <IconButton size="small" onClick={() => navigate('/?nav=workflows')} aria-label="Back" sx={{ color: 'text.secondary' }}>
          <ArrowLeft size={20} strokeWidth={2} />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
          {workflow.name}
        </Typography>
        {saveError ? (
          <Alert severity="error" sx={{ py: 0, maxWidth: 280 }}>
            {saveError}
          </Alert>
        ) : null}
        <Button size="small" variant="outlined" startIcon={<Braces size={16} strokeWidth={2} />} onClick={() => setJsonOpen(true)}>
          JSON
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={16} strokeWidth={2} />}
          onClick={() => persist()}
          disabled={saving}
        >
          Save
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Box
          className="wf-palette vr-scroll-y"
          sx={{ p: 0.75, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mb: 0.5, mt: 0.5, px: 0.75, display: 'block', fontSize: '0.6875rem' }}>
            Events
          </Typography>
          <Box display="flex" flexDirection="column">
            {paletteEventItems.map((item) => {
              const Icon = item.Icon;
              if (!Icon) return null;
              return (
                <Box
                  key={item.type}
                  className="wf-palette-item"
                  draggable
                  onDragStart={(e) => addFromPaletteDragStart(e, item.type)}
                  onClick={() => addFromPaletteClick(item.type)}
                  sx={{ py: 0.75, px: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: item.iconWellBg,
                      color: item.iconColor,
                      flexShrink: 0
                    }}
                  >
                    <Icon size={13} strokeWidth={2} color={item.iconColor} />
                  </Box>
                  <Typography component="span" noWrap sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                    {item.title}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mt: 1, mb: 0.5, px: 0.75, display: 'block', fontSize: '0.6875rem' }}>
            Nodes
          </Typography>
          <Box display="flex" flexDirection="column">
            {PALETTE_NODE_TYPES.map((item) => {
              const Icon = item.Icon;
              return (
                <Box
                  key={item.type}
                  className="wf-palette-item"
                  draggable
                  onDragStart={(e) => addFromPaletteDragStart(e, item.type)}
                  onClick={() => addFromPaletteClick(item.type)}
                  sx={{ py: 0.75, px: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: item.iconWellBg,
                      color: item.iconColor,
                      flexShrink: 0
                    }}
                  >
                    <Icon size={13} strokeWidth={2} color={item.iconColor} />
                  </Box>
                  <Typography component="span" noWrap sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                    {item.title}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mt: 1, mb: 0.5, px: 0.75, display: 'block', fontSize: '0.6875rem' }}>
            Tools
          </Typography>
          {aiTools.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, px: 0.75 }}>
              Add tools under Admin → AI tools. They appear here for Text Generation.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column">
              {aiTools.map((tool) => (
                <Box
                  key={tool.id}
                  className="wf-palette-item"
                  draggable
                  onDragStart={(e) =>
                    addFromPaletteDragStart(e, {
                      kind: 'wfToolRef',
                      toolId: tool.id,
                      name: tool.name,
                      description: tool.description
                    })
                  }
                  onClick={() =>
                    addFromPaletteClick('wfToolRef', {
                      toolId: tool.id,
                      name: tool.name,
                      description: tool.description
                    })
                  }
                  sx={{ py: 0.75, px: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(245, 158, 11, 0.14)',
                      color: '#f59e0b',
                      flexShrink: 0
                    }}
                  >
                    <Wrench size={13} strokeWidth={2} />
                  </Box>
                  <Typography component="span" noWrap sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                    {tool.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

        </Box>

        <Box ref={paneRef} sx={{ flex: 1, minWidth: 0, position: 'relative' }} onDragOver={onDragOver} onDrop={onDrop}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectEnd={onConnectEnd}
            isValidConnection={isValidConnection}
            onNodeDragStart={onNodeDragStart}
            onBeforeDelete={onBeforeDelete}
            onPaneContextMenu={onPaneContextMenu}
            onNodeContextMenu={onNodeContextMenu}
            nodeTypes={wfNodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={{ type: 'bleu', animated: true }}
            connectionLineComponent={(props) => <BleuConnectionLine {...props} />}
            fitView={!merged.viewport}
            fitViewOptions={{ padding: 0.2, duration: 0 }}
            autoPanOnNodeDrag={false}
            autoPanOnConnect={false}
            panOnDrag={[1]}
            selectionOnDrag
            proOptions={{ hideAttribution: true }}
            colorMode="dark"
            className="bleu-workflow"
            deleteKeyCode={['Backspace', 'Delete']}
            onMoveEnd={onMoveEnd}
          >
            <Background variant="dots" gap={20} size={1} color="rgba(255, 255, 255, 0.145)" />
            <Controls showInteractive={false} fitViewOptions={{ duration: 0 }}>
              <ControlButton
                type="button"
                className="wf-controls-map-toggle"
                onClick={() => setMiniMapVisible((v) => !v)}
                title={miniMapVisible ? 'Hide minimap' : 'Show minimap'}
                aria-label={miniMapVisible ? 'Hide minimap' : 'Show minimap'}
                aria-pressed={miniMapVisible}
              >
                <MapIcon size={12} strokeWidth={2} aria-hidden />
              </ControlButton>
            </Controls>
            {miniMapVisible ? (
              <MiniMap
                nodeStrokeWidth={2}
                zoomable
                pannable
                maskColor="rgba(0, 0, 0, 0.28)"
                maskStrokeColor="rgba(255, 255, 255, 0.12)"
                nodeColor="rgba(99, 102, 241, 0.42)"
                nodeStrokeColor="rgba(255, 255, 255, 0.12)"
                bgColor="rgba(24, 24, 27, 0.5)"
              />
            ) : null}
          </ReactFlow>
        </Box>

        {selectedNodes.length > 0 ? (
          <Box className="wf-inspector" sx={{ p: 1.5, overflow: 'auto' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
              Inspector
            </Typography>
            {!primarySelected ? (
              <Typography variant="body2" color="text.secondary">
                {selectedNodes.length} nodes selected
              </Typography>
            ) : (
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Typography variant="caption" color="text.secondary">
                  Type: {primarySelected.type}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Id: {primarySelected.id}
                </Typography>
                <TextField
                  label="Label"
                  fullWidth
                  value={primarySelected.data?.label ?? ''}
                  onChange={(e) => updateSelectedData({ label: e.target.value })}
                />
                <TextField
                  label="Hint"
                  fullWidth
                  multiline
                  minRows={2}
                  value={primarySelected.data?.hint ?? ''}
                  onChange={(e) => updateSelectedData({ hint: e.target.value })}
                />
                {primarySelected.type === 'wfTextGen' ? (
                  <FormControl fullWidth size="small">
                    <InputLabel id="wf-insp-model">Model</InputLabel>
                    <Select
                      labelId="wf-insp-model"
                      label="Model"
                      value={primarySelected.data?.model ?? 'gpt-4o-mini'}
                      onChange={(e) => updateSelectedData({ model: e.target.value })}
                    >
                      {TEXT_GEN_MODELS.map((m) => (
                        <MenuItem key={m.id} value={m.id} dense>
                          {m.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
                {primarySelected.type === 'wfToolRef' ? (
                  <TextField
                    label="Tool id"
                    fullWidth
                    size="small"
                    value={primarySelected.data?.toolId ?? ''}
                    InputProps={{ readOnly: true }}
                  />
                ) : null}
              </Box>
            )}
          </Box>
        ) : null}
      </Box>

      <Menu
        className="wf-ctx-menu wf-ctx-menu--add-nodes"
        open={Boolean(ctxMenu)}
        onClose={closeCtxMenu}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.mouseY, left: ctxMenu.mouseX } : undefined}
        transitionDuration={0}
        TransitionProps={{ timeout: 0 }}
        MenuListProps={{ dense: true, sx: { py: 0.5, px: 0.5 } }}
        PaperProps={{
          elevation: 0,
          sx: {
            ...CTX_MENU_PAPER_SX,
            bgcolor: '#18181b'
          }
        }}
      >
        <MenuItem disabled sx={{ ...CTX_ADD_NODE_ITEM_SX, opacity: '1 !important', cursor: 'default' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Events
          </Typography>
        </MenuItem>
        {paletteEventItems.map((item) => {
          const PIcon = item.Icon;
          if (!PIcon) return null;
          return (
            <MenuItem
              key={item.type}
              onClick={() => pickNodeTypeFromMenu(item.type)}
              sx={CTX_ADD_NODE_ITEM_SX}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  bgcolor: item.iconWellBg
                }}
              >
                <PIcon size={13} strokeWidth={2} color={item.iconColor} />
              </Box>
              <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                {item.title}
              </Typography>
            </MenuItem>
          );
        })}
        <MenuItem disabled sx={{ ...CTX_ADD_NODE_ITEM_SX, opacity: '1 !important', cursor: 'default' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Nodes
          </Typography>
        </MenuItem>
        {PALETTE_NODE_TYPES.map((item) => {
          const PIcon = item.Icon;
          return (
            <MenuItem
              key={item.type}
              onClick={() => pickNodeTypeFromMenu(item.type)}
              sx={CTX_ADD_NODE_ITEM_SX}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  bgcolor: item.iconWellBg
                }}
              >
                <PIcon size={13} strokeWidth={2} color={item.iconColor} />
              </Box>
              <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                {item.title}
              </Typography>
            </MenuItem>
          );
        })}
        <MenuItem disabled sx={{ ...CTX_ADD_NODE_ITEM_SX, opacity: '1 !important', cursor: 'default' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Tools
          </Typography>
        </MenuItem>
        {aiTools.map((tool) => (
          <MenuItem key={tool.id} onClick={() => pickToolFromMenu(tool)} sx={CTX_ADD_NODE_ITEM_SX}>
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: 'rgba(245, 158, 11, 0.14)',
                color: '#f59e0b'
              }}
            >
              <Wrench size={13} strokeWidth={2} />
            </Box>
            <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
              {tool.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        className="wf-ctx-menu wf-ctx-menu--node-actions"
        open={Boolean(nodeCtx)}
        onClose={closeNodeCtx}
        anchorReference="anchorPosition"
        anchorPosition={nodeCtx ? { top: nodeCtx.mouseY, left: nodeCtx.mouseX } : undefined}
        transitionDuration={0}
        TransitionProps={{ timeout: 0 }}
        MenuListProps={{ dense: true, sx: { py: 0.5, px: 0.5 } }}
        PaperProps={{
          elevation: 0,
          sx: {
            ...CTX_MENU_PAPER_SX,
            bgcolor: '#18181b'
          }
        }}
      >
        <MenuItem
          onClick={() => {
            if (nodeCtx) {
              const n = nodesRef.current.find((x) => x.id === nodeCtx.nodeId);
              if (n) {
                const ids = new Set([n.id]);
                const subEdges = edgesRef.current.filter((e) => ids.has(e.source) && ids.has(e.target));
                try {
                  sessionStorage.setItem(
                    'flowcore_wf_clipboard',
                    JSON.stringify({ v: 1, nodes: [n], edges: subEdges })
                  );
                } catch (e) {
                  setSaveError('Clipboard write failed');
                }
              }
            }
            closeNodeCtx();
          }}
          sx={CTX_NODE_ACTION_ITEM_SX}
        >
          <Copy size={16} strokeWidth={2} style={{ color: 'rgba(161,161,170,0.95)', flexShrink: 0 }} />
          <Typography component="span" sx={{ flex: 1, minWidth: 0, fontWeight: 500, fontSize: '0.8125rem' }}>
            Copy
          </Typography>
          <CtxMenuShortcut>{keyHints.copy}</CtxMenuShortcut>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (nodeCtx) deleteNodeById(nodeCtx.nodeId);
            closeNodeCtx();
          }}
          sx={CTX_NODE_ACTION_ITEM_SX}
        >
          <Trash2 size={16} strokeWidth={2} style={{ color: '#fca5a5', flexShrink: 0 }} />
          <Typography component="span" sx={{ flex: 1, minWidth: 0, fontWeight: 500, fontSize: '0.8125rem' }}>
            Delete
          </Typography>
          <CtxMenuShortcut>{keyHints.del}</CtxMenuShortcut>
        </MenuItem>
      </Menu>

      <Dialog
        open={jsonOpen}
        onClose={() => setJsonOpen(false)}
        maxWidth="md"
        fullWidth
        className="wf-json-dialog"
        PaperProps={{
          sx: {
            bgcolor: '#18181b',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>Graph JSON (logic + meta)</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            value={jsonPreview}
            multiline
            fullWidth
            minRows={16}
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontSize: 12,
                lineHeight: 1.5,
                color: '#e4e4e7',
                bgcolor: 'rgba(0,0,0,0.35)',
                alignItems: 'flex-start',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                '& textarea': {
                  resize: 'vertical',
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent'
                },
                '& textarea::-webkit-scrollbar': { width: 8, height: 8 },
                '& textarea::-webkit-scrollbar-track': { background: 'transparent' },
                '& textarea::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 6
                },
                '& textarea::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.35)'
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Copy size={16} strokeWidth={2} />}
            onClick={() => {
              navigator.clipboard.writeText(jsonPreview);
            }}
          >
            Copy
          </Button>
          <Button onClick={() => setJsonOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function WorkflowEditorPage() {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuthContext();
  const [state, setState] = useState({ status: 'loading', workflow: null, message: '' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE_URL}/api/workflows/${encodeURIComponent(workflowId)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || `Load failed (${res.status})`);
        }
        const data = await res.json();
        if (cancelled) return;
        setState({
          status: 'ok',
          workflow: {
            id: data.id,
            name: data.name,
            categoryId: data.categoryId || 'other',
            graph: data.graph,
            graphMeta: data.graphMeta
          },
          message: ''
        });
      } catch (e) {
        if (!cancelled) {
          setState({ status: 'error', workflow: null, message: e.message || 'Load failed' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workflowId, getToken]);

  if (state.status === 'loading') {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#09090b',
          gap: 2
        }}
      >
        <CircularProgress size={28} sx={{ color: '#a78bfa' }} />
        <Typography color="text.secondary">Loading workflow…</Typography>
      </Box>
    );
  }

  if (state.status === 'error') {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 3, bgcolor: '#09090b' }}>
        <Alert severity="error">{state.message}</Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <ReactFlowProvider>
      <WorkflowEditorCanvas workflow={state.workflow} />
    </ReactFlowProvider>
  );
}
